<script setup lang="ts">
import gsap from 'gsap'
import { useBlogStore } from '~/stores/BlogStore'
import type { SectionArticleListRecord } from '~/types/datocms'

interface ArticlePageFilterProps {
  categories: SectionArticleListRecord['categories']
  allArticlesLabel?: string
  allArticlesCountLabel?: string
}

withDefaults(defineProps<ArticlePageFilterProps>(), {
  allArticlesLabel: 'All',
  allArticlesCountLabel: 'Count:'
})

const store = useBlogStore()
const route = useRoute()

const isShowAllArticles = computed(() => (Object.keys(route.query).length === 0 ? true : false))
const isMounted = ref(false)
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  isMounted.value = true
  if (ctxRef.value) {
    ctx = gsap.context((self) => {
      const fxTabs = self.selector?.('.fx-tab')

      if (fxTabs) {
        gsap.set([fxTabs], { y: '100%' })
        gsap.to([fxTabs], {
          y: 0,
          ease: 'expo.out',
          stagger: 0.15,
          duration: 1.2
        })
      }
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    data-lenis-prevent
    class="mb-2 flex w-full items-center gap-2 overflow-y-hidden overflow-x-scroll pb-3 lg:mb-0 lg:pb-0"
    :class="isMounted ? 'visible' : 'invisible'"
  >
    <ArticlePageFilterItem
      class="fx-tab"
      :to="route.path"
      :is-active="isShowAllArticles"
      :is-disabled="store.isLoading"
    >
      {{ allArticlesLabel }}
    </ArticlePageFilterItem>
    <ArticlePageFilterItem
      v-for="category in categories"
      :key="category.slug!"
      class="fx-tab"
      :to="{ path: route.path, query: { category: category.slug ?? '' } }"
      :is-active="route.query.category === category.slug"
      :is-disabled="store.isLoading"
    >
      {{ category.name }}
    </ArticlePageFilterItem>
  </div>
</template>
